import React, { useMemo } from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  GIVING_BY_CAMPAIGN_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/givingByCampaignColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { CAMPAIGN_STATUS_DISPLAY, campaignStatusDisplay } from '../../shared/givingHelpers';
import GivingBadge from '../../shared/components/GivingBadge';

const EMPTY_FUNDS = [];

const CAMPAIGN_TYPES = [
  { value: 'annual', label: 'Annual' },
  { value: 'capital', label: 'Capital' },
  { value: 'endowment', label: 'Endowment' },
  { value: 'event_based', label: 'Event Based' },
  { value: 'emergency_special_appeal', label: 'Emergency / Special Appeal' },
];
const STATUSES = Object.entries(CAMPAIGN_STATUS_DISPLAY).map(([value, { label }]) => ({ value, label }));
const TYPE_LABELS = CAMPAIGN_TYPES.reduce((acc, t) => ({ ...acc, [t.value]: t.label }), {});

const buildFilterGroups = (funds) => {
  const groups = [
    {
      label: 'Campaign',
      filters: [
        { id: 'campaign_type', label: 'Type', type: 'checkbox', options: CAMPAIGN_TYPES },
        { id: 'status', label: 'Status', type: 'checkbox', options: STATUSES },
      ],
    },
  ];

  if (funds && funds.length > 0) {
    groups.push({
      label: 'Fund',
      filters: [
        {
          id: 'fund_id',
          label: 'Fund',
          type: 'checkbox',
          options: funds.map((f) => ({ value: String(f.id), label: f.name })),
        },
      ],
    });
  }

  return groups;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.campaign_type && activeFilters.campaign_type.length > 0) params.campaign_type = activeFilters.campaign_type;
  if (activeFilters.status && activeFilters.status.length > 0) params.status = activeFilters.status;
  if (activeFilters.fund_id && activeFilters.fund_id.length > 0) params.fund_id = activeFilters.fund_id;
  return params;
};

const StatusBadge = ({ status }) => {
  if (!status) return '—';
  const { label, variant } = campaignStatusDisplay(status);
  return <GivingBadge label={label} variant={variant} style={{ whiteSpace: 'nowrap' }} />;
};

// Bar fills to 100% max; the label shows the true value so over-goal reads e.g. "120%".
const ProgressBar = ({ value }) => {
  const pct = value || 0;
  const over = pct > 100;
  return (
    <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--sl-bg-tertiary, #f2f4f7)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', borderRadius: 3, background: over ? 'var(--sl-fg-success, #067647)' : 'var(--sl-fg-brand, #1982c4)' }} />
      </div>
      <span className="text-nowrap" style={{ fontSize: 13, color: 'var(--sl-text-secondary, #344054)', fontWeight: 500 }}>{pct}%</span>
    </div>
  );
};

// The campaign name links to the full-page donations drill-down, forwarding the
// active date window so the listed gifts reconcile to this row's Total Raised.
const drilldownHref = (teamId, row, dateRange) => {
  const params = new URLSearchParams({ drilldown_id: row.id });
  if (dateRange && dateRange.start) params.set('start_date', dateRange.start);
  if (dateRange && dateRange.end) params.set('end_date', dateRange.end);
  return `/teams/${teamId}/reporting/donations/giving-by-campaign/gifts?${params.toString()}`;
};

const CONFIG = {
  pageTitle: 'Giving by Campaign',
  pageSubtitle: 'Total raised per giving campaign with donor counts, goal progress, and outstanding pledge balances. Shows lifetime totals by default — pick a date range to window gifts by gift date and pledges by pledge date.',
  sidebarKey: 'donations_giving_by_campaign',
  defaultPreset: 'all_time',
  // Single option => the "View by" toggle is hidden; giving has no event-date mode.
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: GIVING_BY_CAMPAIGN_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingGivingByCampaignColumns_${teamId}`,
  firstColumnId: 'name',
  defaultSortRules: [{ field: 'total_raised', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-campaign/data.json`,
  detailResponseKey: 'campaigns',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-campaign/export`,
  detailErrorMessage: 'Failed to load campaign data.',
  detailTableTitle: 'Campaigns',
  emptyMessage: 'No giving campaigns found.',
  searchPlaceholder: 'Search campaigns...',
  paginationLabel: 'campaigns',
  itemLabel: (count) => `campaign${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,
  filterTitle: 'Filter Campaigns',
  filterSubtitle: 'Apply filters to narrow campaigns.',

  buildKpiCards: (current, prior) => {
    const p = prior || {};
    return [
      { label: 'Total Raised', value: compactCurrency(current.total_raised || 0), current: current.total_raised || 0, prior: p.total_raised || 0 },
      { label: 'Campaigns', value: (current.campaign_count || 0).toLocaleString(), current: current.campaign_count || 0, prior: p.campaign_count || 0 },
      { label: 'Unique Donors', value: (current.donor_count || 0).toLocaleString(), current: current.donor_count || 0, prior: p.donor_count || 0 },
      { label: 'Total Gifts', value: (current.gift_count || 0).toLocaleString(), current: current.gift_count || 0, prior: p.gift_count || 0 },
    ];
  },
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, dateRange, team }) => {
    switch (col.id) {
      case 'name': return (
        <a href={drilldownHref(team.id, row, dateRange)} style={{ color: 'var(--sl-fg-brand, #1982c4)', fontWeight: 600 }}>
          {row.name || '—'}
        </a>
      );
      case 'campaign_type': return TYPE_LABELS[row.campaign_type] || row.campaign_type || '—';
      case 'status': return <StatusBadge status={row.status} />;
      case 'goal_amount': return row.goal_amount ? cf.format(row.goal_amount) : '—';
      case 'total_raised': return cf.format(row.total_raised || 0);
      case 'donor_count': return (row.donor_count || 0).toLocaleString();
      case 'gift_count': return (row.gift_count || 0).toLocaleString();
      case 'progress': return <ProgressBar value={row.progress || 0} />;
      case 'pledged_amount': return cf.format(row.pledged_amount || 0);
      case 'pledge_received_amount': return cf.format(row.pledge_received_amount || 0);
      case 'outstanding_amount': return cf.format(row.outstanding_amount || 0);
      case 'raised_plus_outstanding': return cf.format(row.raised_plus_outstanding || 0);
      case 'start_date': return <span className="text-nowrap">{row.start_date ? formatEventDate(row.start_date) : '—'}</span>;
      case 'end_date': return <span className="text-nowrap">{row.end_date ? formatEventDate(row.end_date) : '—'}</span>;
      default: return '—';
    }
  },
};

function ReportingGivingByCampaignPage(props) {
  const funds = props.funds || EMPTY_FUNDS;
  const config = useMemo(
    () => ({ ...CONFIG, buildFilterGroups: () => buildFilterGroups(funds) }),
    [funds]
  );
  return <SalesDetailPage {...props} config={config} />;
}

export default ReportingGivingByCampaignPage;
